import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// ─── Constantes ───────────────────────────────────────────────────────────────

// Offre unique : 1 séance / semaine
// Tarifs ajustables via le panel ⚙️ Tarifs dans l'interface
const TARIF_PAR_SEANCE_30MIN = 40; // DH (80 DH/h)
const TARIF_PAR_SEANCE_60MIN = 80; // DH (80 DH/h)

const VALID_BANK_CODES = {
  '001': 'Bank Al-Maghrib',
  '007': 'Attijariwafa Bank',
  '011': 'BMCE Bank / Bank of Africa',
  '013': 'Banque Populaire',
  '014': 'Banque Populaire',
  '017': 'BMCI',
  '022': 'Crédit du Maroc',
  '040': 'Banque Populaire',
  '046': 'Crédit Agricole du Maroc',
  '080': 'CIH Bank',
  '101': 'Banque Populaire Casablanca',
  '102': 'Banque Populaire Rabat',
  '103': 'Banque Populaire Fès',
  '104': 'Banque Populaire Marrakech',
  '105': 'Banque Populaire Oujda',
  '106': 'Banque Populaire Agadir',
  '107': 'Banque Populaire Tanger',
  '108': 'Banque Populaire Meknès',
  '120': 'Société Générale Maroc',
  '135': 'CDM Capital',
  '147': 'Al Barid Bank',
  '181': 'CFG Bank',
  '195': 'Arab Bank',
  '196': 'Citibank Maroc',
  '197': 'HSBC Maroc',
  '200': 'Banque Centrale Populaire',
  '230': 'Bank of Africa',
};

// Types d'abonnements actifs
// - 'essai'       : 1 crédit, pas de recharge
// - 'une_seance'  : 1 crédit/semaine, recharge chaque lundi (offre unique)
const STATUTS_PAYABLES = ['confirmé', 'terminé', 'complété'];

// ─── Vérifications de sécurité du calcul ──────────────────────────────────────

/**
 * Recalcule et vérifie le montant d'un prof AVANT de confirmer le paiement.
 * Compare le calcul frontend vs une requête fraîche Supabase.
 * Retourne { ok, montantAttendu, montantCalcule, delta }
 */
async function verifierCalculAvantPaiement(supabaseClient, prof, moisSelectionne) {
  const [annee, mois] = moisSelectionne.split('-');
  const debutMois = `${annee}-${mois}-01`;
  const finMois = new Date(annee, mois, 0);
  const finMoisStr = `${annee}-${mois}-${String(finMois.getDate()).padStart(2, '0')}`;

  // Requête fraîche indépendante du state React
  const { data: sessions, error } = await supabaseClient
    .from('reservations')
    .select('id, statut, duree_minutes, included_in_monthly_payout')
    .eq('prof_id', prof.id)
    .gte('date', debutMois)
    .lte('date', finMoisStr);

  if (error) return { ok: false, erreur: error.message };

  // Recalcul indépendant
  const payables = sessions.filter(s =>
    STATUTS_PAYABLES.includes(s.statut) && !s.included_in_monthly_payout
  );
  const montantAttendu = payables.reduce((acc, s) => {
    return acc + (s.duree_minutes === 60 ? TARIF_PAR_SEANCE_60MIN : TARIF_PAR_SEANCE_30MIN);
  }, 0);

  const delta = Math.abs(montantAttendu - prof.montant);
  const ok = delta < 0.01; // Tolérance 0.01 DH pour les flottants

  return {
    ok,
    montantAttendu,
    montantCalcule: prof.montant,
    delta,
    nbSessionsVerifiees: payables.length,
    nbSessionsAffichees: prof.sessionCount,
  };
}
const STATUTS_PROBLEMATIQUES = ['en_attente', 'expiré'];

// ─── Utilitaires ──────────────────────────────────────────────────────────────

function getMoisOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    options.push({ value: val, label });
  }
  return options;
}

function validateRIB(rib) {
  if (!rib) return { valid: false, error: 'RIB manquant' };
  const digits = rib.replace(/\s/g, '');
  if (digits.length !== 24) return { valid: false, error: `RIB doit faire 24 chiffres (${digits.length} trouvés)` };
  if (!/^\d+$/.test(digits)) return { valid: false, error: 'RIB doit contenir uniquement des chiffres' };
  const bankCode = digits.slice(0, 3);
  if (!VALID_BANK_CODES[bankCode]) return { valid: false, error: `Code banque "${bankCode}" non reconnu` };
  return { valid: true, bankName: VALID_BANK_CODES[bankCode] };
}

// IBAN : identifiant international. Format + CLÉ DE CONTRÔLE mod-97 (détecte ~99% des fautes de frappe).
function validateIBAN(iban) {
  if (!iban) return { valid: false, error: 'IBAN manquant' };
  const clean = iban.replace(/\s/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(clean)) return { valid: false, error: 'Format IBAN invalide' };
  // Contrôle mod-97 : déplacer les 4 premiers caractères à la fin, remplacer les lettres (A=10…Z=35), mod 97 == 1
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  const expanded = rearranged.replace(/[A-Z]/g, ch => (ch.charCodeAt(0) - 55).toString());
  let remainder = 0;
  for (let i = 0; i < expanded.length; i++) {
    remainder = (remainder * 10 + (expanded.charCodeAt(i) - 48)) % 97;
  }
  if (remainder !== 1) return { valid: false, error: 'IBAN invalide (clé de contrôle erronée)' };
  return { valid: true, country: clean.slice(0, 2) };
}

// Un prof est payable s'il a un RIB marocain valide OU un IBAN valide.
function validerCoordonnees(prof) {
  const rib = validateRIB(prof.rib_maroc);
  if (rib.valid) return { valid: true, type: 'RIB', display: formatRIB(prof.rib_maroc), bankName: rib.bankName };
  const iban = validateIBAN(prof.iban_maroc);
  if (iban.valid) return { valid: true, type: 'IBAN', display: prof.iban_maroc, bankName: prof.bank_name || `IBAN ${iban.country}` };
  return {
    valid: false, type: null, display: '—', bankName: prof.bank_name || '—',
    error: prof.rib_maroc ? rib.error : (prof.iban_maroc ? iban.error : 'Ni RIB marocain ni IBAN renseigné')
  };
}

function formatRIB(rib) {
  if (!rib) return '—';
  const digits = rib.replace(/\s/g, '');
  return digits.replace(/(\d{3})(\d{3})(\d{16})(\d{2})/, '$1 $2 $3 $4');
}

function formatMontant(montant) {
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(montant || 0) + ' DH';
}

function detecterErreursProf(prof, sessions) {
  const erreurs = [];
  const avertissements = [];

  // — Coordonnées bancaires (RIB marocain OU IBAN)
  const coord = validerCoordonnees(prof);
  if (!coord.valid) erreurs.push({ code: 'COORDONNEES_INVALIDES', msg: coord.error });

  // — Nom titulaire
  if (!prof.account_holder?.trim()) avertissements.push({ code: 'TITULAIRE_MANQUANT', msg: 'Nom du titulaire de compte non renseigné' });

  // — Sessions problématiques
  const sessionsProblematiques = sessions.filter(s => STATUTS_PROBLEMATIQUES.includes(s.statut));
  if (sessionsProblematiques.length > 0) {
    avertissements.push({
      code: 'SESSIONS_EN_ATTENTE',
      msg: `${sessionsProblematiques.length} session(s) avec statut ambigu (${[...new Set(sessionsProblematiques.map(s => s.statut))].join(', ')})`
    });
  }

  // — Aucune session payable
  const sessionsPayables = sessions.filter(s => STATUTS_PAYABLES.includes(s.statut));
  if (sessions.length > 0 && sessionsPayables.length === 0) {
    avertissements.push({ code: 'AUCUNE_SESSION_PAYABLE', msg: 'Aucune session confirmée/terminée ce mois' });
  }

  // — Durée manquante
  const sansduree = sessionsPayables.filter(s => !s.duree_minutes || (s.duree_minutes !== 30 && s.duree_minutes !== 60));
  if (sansduree.length > 0) {
    avertissements.push({ code: 'DUREE_INVALIDE', msg: `${sansduree.length} session(s) avec durée invalide (ni 30 ni 60 min)` });
  }

  // — Déjà payé ce mois
  if (prof.deja_paye) avertissements.push({ code: 'DEJA_PAYE', msg: `Déjà payé ce mois (réf: ${prof.ref_paiement || '?'})` });

  // — Sessions déjà incluses dans un payout précédent (via included_in_monthly_payout)
  const sessionsDejaPayees = sessions.filter(s => s.included_in_monthly_payout);
  if (sessionsDejaPayees.length > 0) {
    const refs = [...new Set(sessionsDejaPayees.map(s => s.payout_reference).filter(Boolean))];
    avertissements.push({
      code: 'SESSIONS_DEJA_PAYEES',
      msg: `${sessionsDejaPayees.length} séance(s) déjà incluse(s) dans un payout précédent${refs.length ? ` (réf: ${refs.join(', ')})` : ''} — exclues du calcul`
    });
  }

  return { erreurs, avertissements };
}

function calculerMontantProf(sessions) {
  // Exclure les séances déjà incluses dans un payout précédent
  const payables = sessions.filter(s => STATUTS_PAYABLES.includes(s.statut) && !s.included_in_monthly_payout);
  const dejaPayees = sessions.filter(s => s.included_in_monthly_payout);
  let total = 0;
  // Offre unique 1 séance/semaine
  const details = { seances30: 0, seances60: 0, seancesAutres: 0, dejaPayees: dejaPayees.length };

  payables.forEach(s => {
    if (s.duree_minutes === 30) {
      total += TARIF_PAR_SEANCE_30MIN;
      details.seances30++;
    } else if (s.duree_minutes === 60) {
      total += TARIF_PAR_SEANCE_60MIN;
      details.seances60++;
    } else {
      total += TARIF_PAR_SEANCE_30MIN; // défaut
      details.seancesAutres++;
    }
  });

  return { total, details, count: payables.length, countDejaPayees: dejaPayees.length };
}

// ─── Composants ───────────────────────────────────────────────────────────────

function Badge({ type, children }) {
  const styles = {
    success: { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' },
    error: { background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' },
    warning: { background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047' },
    info: { background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' },
    neutral: { background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1' },
    paid: { background: '#f0fdf4', color: '#15803d', border: '1px solid #4ade80' },
  };
  return (
    <span style={{
      ...styles[type] || styles.neutral,
      padding: '2px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      whiteSpace: 'nowrap',
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
    }}>
      {children}
    </span>
  );
}

function ErreurPanel({ erreurs, avertissements }) {
  if (erreurs.length === 0 && avertissements.length === 0) return null;
  return (
    <div style={{ marginTop: 8 }}>
      {erreurs.map((e, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 3, color: '#dc2626', fontSize: 12 }}>
          <span>🚫</span><span>{e.msg}</span>
        </div>
      ))}
      {avertissements.map((a, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 3, color: '#d97706', fontSize: 12 }}>
          <span>⚠️</span><span>{a.msg}</span>
        </div>
      ))}
    </div>
  );
}

function ModalDetails({ prof, sessions, onClose }) {
  if (!prof) return null;
  const { total, details } = calculerMontantProf(sessions);
  const { erreurs, avertissements } = detecterErreursProf(prof, sessions);
  const coord = validerCoordonnees(prof);
  const payables = sessions.filter(s => STATUTS_PAYABLES.includes(s.statut) && !s.included_in_monthly_payout);
  const nonPayables = sessions.filter(s => !STATUTS_PAYABLES.includes(s.statut));
  const dejaPayeesModal = sessions.filter(s => s.included_in_monthly_payout);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, maxWidth: 700, width: '100%',
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
      }} onClick={e => e.stopPropagation()}>

        {/* Header modal */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
          borderRadius: '16px 16px 0 0', color: 'white',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
                {prof.prenom} {prof.nom}
              </h2>
              <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: 13 }}>{prof.email}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: '#34d399' }}>{formatMontant(total)}</div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>{payables.length} séance(s) payable(s)</div>
            </div>
          </div>
        </div>

        <div style={{ padding: 24 }}>

          {/* Erreurs et avertissements */}
          {(erreurs.length > 0 || avertissements.length > 0) && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Alertes détectées
              </h4>
              {erreurs.map((e, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 14px', background: '#fee2e2', borderRadius: 8, marginBottom: 6, border: '1px solid #fca5a5' }}>
                  <span>🚫</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#991b1b' }}>{e.code}</div>
                    <div style={{ fontSize: 13, color: '#7f1d1d' }}>{e.msg}</div>
                  </div>
                </div>
              ))}
              {avertissements.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 14px', background: '#fef9c3', borderRadius: 8, marginBottom: 6, border: '1px solid #fde047' }}>
                  <span>⚠️</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 12, color: '#854d0e' }}>{a.code}</div>
                    <div style={{ fontSize: 13, color: '#78350f' }}>{a.msg}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Infos bancaires */}
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Informations bancaires
            </h4>
            <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>RIB / IBAN</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600, color: coord.valid ? '#1e40af' : '#dc2626' }}>
                    {coord.display}
                  </div>
                  {!coord.valid && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>{coord.error}</div>}
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>BANQUE</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                    {coord.bankName}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>TITULAIRE</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{prof.account_holder || '—'}</div>
                </div>
                {prof.iban_maroc && (
                  <div>
                    <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>IBAN</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 13, color: '#1e40af' }}>{prof.iban_maroc}</div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Calcul détaillé */}
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Détail du calcul
            </h4>
            <div style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {details.seances30 > 0 && (
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 14px', fontSize: 14 }}>Séances 30 min (40 DH)</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: 14 }}>× {details.seances30}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 14, color: '#64748b' }}>{formatMontant(TARIF_PAR_SEANCE_30MIN)} / séance</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>{formatMontant(details.seances30 * TARIF_PAR_SEANCE_30MIN)}</td>
                    </tr>
                  )}
                  {details.seances60 > 0 && (
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 14px', fontSize: 14 }}>Séances 60 min (80 DH)</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: 14 }}>× {details.seances60}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 14, color: '#64748b' }}>{formatMontant(TARIF_PAR_SEANCE_60MIN)} / séance</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>{formatMontant(details.seances60 * TARIF_PAR_SEANCE_60MIN)}</td>
                    </tr>
                  )}
                  {details.seancesAutres > 0 && (
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 14px', fontSize: 14, color: '#d97706' }}>⚠️ Séances durée inconnue</td>
                      <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: 14 }}>× {details.seancesAutres}</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontSize: 14, color: '#64748b' }}>Tarif 30 min appliqué</td>
                      <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>{formatMontant(details.seancesAutres * TARIF_PAR_SEANCE_30MIN)}</td>
                    </tr>
                  )}
                  <tr style={{ background: '#0f172a' }}>
                    <td colSpan={3} style={{ padding: '12px 14px', fontWeight: 700, fontSize: 14, color: '#94a3b8' }}>TOTAL À VIRER</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 800, fontSize: 18, color: '#34d399' }}>{formatMontant(total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Liste des séances */}
          <div>
            <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Séances du mois ({sessions.length} total)
            </h4>
            <div style={{ maxHeight: 200, overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f1f5f9', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Date</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, color: '#475569' }}>Heure</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#475569' }}>Durée</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#475569' }}>Statut</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, color: '#475569' }}>Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s, i) => {
                    const isPay = STATUTS_PAYABLES.includes(s.statut);
                    const montant = isPay ? (s.duree_minutes === 60 ? TARIF_PAR_SEANCE_60MIN : TARIF_PAR_SEANCE_30MIN) : 0;
                    return (
                      <tr key={s.id || i} style={{ borderTop: '1px solid #f1f5f9', background: isPay ? 'white' : '#fffbeb' }}>
                        <td style={{ padding: '8px 12px', color: '#334155' }}>{s.date}</td>
                        <td style={{ padding: '8px 12px', color: '#334155' }}>{s.heure_debut}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', color: '#334155' }}>{s.duree_minutes || '?'} min</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          <Badge type={isPay ? 'success' : 'warning'}>{s.statut}</Badge>
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: isPay ? '#15803d' : '#94a3b8' }}>
                          {isPay ? formatMontant(montant) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div style={{ padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{
            padding: '10px 24px', background: '#f1f5f9', border: 'none', borderRadius: 8,
            fontWeight: 600, cursor: 'pointer', color: '#475569', fontSize: 14,
          }}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

// ─── Composant principal ───────────────────────────────────────────────────────

export default function AdminPayouts() {
  const moisOptions = getMoisOptions();
  const [moisSelectionne, setMoisSelectionne] = useState(moisOptions[0].value);
  const [profsData, setProfsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selection, setSelection] = useState(new Set());
  const [modalProf, setModalProf] = useState(null);
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [filtreErreur, setFiltreErreur] = useState(false);
  const [search, setSearch] = useState('');
  const [showMarquerModal, setShowMarquerModal] = useState(false);
  const [refVirement, setRefVirement] = useState('');
  const [marquerLoading, setMarquerLoading] = useState(false);
  const [verifResult, setVerifResult] = useState(null); // Résultat vérification calcul
  const [exportLoading, setExportLoading] = useState(false);
  const [tarifCustom30, setTarifCustom30] = useState(TARIF_PAR_SEANCE_30MIN);
  const [tarifCustom60, setTarifCustom60] = useState(TARIF_PAR_SEANCE_60MIN);
  const [showTarifsPanel, setShowTarifsPanel] = useState(false);
  const [historiquePayouts, setHistoriquePayouts] = useState([]);
  const [activeTab, setActiveTab] = useState('calcul');

  // — Charger les données
  const chargerDonnees = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelection(new Set());

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Session expirée — veuillez vous reconnecter');

      // 1. Récupérer tous les profs validés
      const { data: profsBase, error: erProfs } = await supabase
        .from('profs')
        .select('id, nom, prenom, email, statut_profil')
        .eq('statut_profil', 'valide');
      if (erProfs) throw new Error(`Erreur profs: ${erProfs.message}`);

      // 1b. Coordonnées bancaires depuis la table isolée (RLS admin)
      const { data: banks } = await supabase
        .from('profs_bancaire')
        .select('prof_id, rib_maroc, iban_maroc, bank_name, account_holder');
      const bankByProf = Object.fromEntries((banks || []).map(b => [b.prof_id, b]));
      const profs = (profsBase || []).map(p => ({ ...p, ...(bankByProf[p.id] || {}) }));

      // 2. Récupérer les réservations du mois sélectionné
      const [annee, mois] = moisSelectionne.split('-');
      const debutMois = `${annee}-${mois}-01`;
      const finMois = new Date(annee, mois, 0);
      const finMoisStr = `${annee}-${mois}-${String(finMois.getDate()).padStart(2, '0')}`;

      const { data: reservations, error: erRes } = await supabase
        .from('reservations')
        .select('id, date, heure_debut, heure_fin, duree_minutes, statut, prof_id, included_in_monthly_payout, payout_reference, payout_date')
        .gte('date', debutMois)
        .lte('date', finMoisStr);
      if (erRes) throw new Error(`Erreur réservations: ${erRes.message}`);
      console.log('🔍 [PAYOUTS DEBUG] Mois:', moisSelectionne);
      console.log('🔍 [PAYOUTS DEBUG] Réservations trouvées:', reservations?.length, reservations);
      console.log('🔍 [PAYOUTS DEBUG] Profs trouvés:', profs?.length, profs?.map(p => ({id: p.id, nom: p.nom, prenom: p.prenom})));

      // 3. Récupérer les payouts déjà effectués ce mois
      const { data: payouts } = await supabase
        .from('monthly_payouts')
        .select('professor_id, net_amount, gross_amount, platform_fee, transfer_reference, created_at, status, total_courses, payout_month')
        .gte('created_at', `${debutMois}T00:00:00`)
        .lte('created_at', `${finMoisStr}T23:59:59`);

      const payoutsMap = {};
      (payouts || []).forEach(p => {
        payoutsMap[p.professor_id] = p;
      });

      // 4. Assembler les données par prof
      const data = profs.map(prof => {
        const sessionsProf = reservations.filter(r => r.prof_id === prof.id || r.created_by === prof.id);
        const { total, details, count, countDejaPayees } = calculerMontantProf(sessionsProf);
        const { erreurs, avertissements } = detecterErreursProf(
          { ...prof, deja_paye: !!payoutsMap[prof.id], ref_paiement: payoutsMap[prof.id]?.transfer_reference },
          sessionsProf
        );
        const coord = validerCoordonnees(prof);

        return {
          ...prof,
          sessions: sessionsProf,
          sessionCount: count,
          sessionDejaPayees: countDejaPayees,
          montant: total,
          details,
          erreurs,
          avertissements,
          ribValide: coord.valid,
          coordType: coord.type,
          coordDisplay: coord.display,
          bankName: coord.bankName,
          deja_paye: !!payoutsMap[prof.id],  // professor_id dans monthly_payouts = id dans profs
          ref_paiement: payoutsMap[prof.id]?.transfer_reference,
          pret: coord.valid && !payoutsMap[prof.id] && count > 0 && erreurs.length === 0,
        };
      });

      setProfsData(data);

      // 5. Historique payouts complet
      const { data: histAll } = await supabase
        .from('monthly_payouts')
        .select('id, professor_id, net_amount, gross_amount, platform_fee, transfer_reference, created_at, status, payout_month, payout_method, sessions_count, periode_mois, profs(nom, prenom, email)')
        .order('created_at', { ascending: false })
        .limit(50);
      setHistoriquePayouts(histAll || []);

    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [moisSelectionne]);

  useEffect(() => { chargerDonnees(); }, [chargerDonnees]);

  // — Filtrage
  const profsFiltres = profsData.filter(p => {
    if (search && !`${p.nom} ${p.prenom} ${p.email}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (filtreStatut === 'pret') return p.pret;
    if (filtreStatut === 'paye') return p.deja_paye;
    if (filtreStatut === 'rib_manquant') return !p.ribValide;
    if (filtreStatut === 'avec_sessions') return p.sessionCount > 0;
    if (filtreErreur) return p.erreurs.length > 0 || p.avertissements.length > 0;
    return true;
  });

  // — Statistiques globales
  const stats = {
    totalAPayer: profsFiltres.filter(p => p.pret && selection.has(p.id)).reduce((acc, p) => acc + p.montant, 0),
    profsSelectionnes: selection.size,
    totalProfs: profsData.length,
    ribManquants: profsData.filter(p => !p.ribValide).length,
    dejaPayes: profsData.filter(p => p.deja_paye).length,
    avecErreurs: profsData.filter(p => p.erreurs.length > 0).length,
    avecAvertissements: profsData.filter(p => p.avertissements.length > 0 && p.erreurs.length === 0).length,
    totalSessions: profsData.reduce((acc, p) => acc + p.sessionCount, 0),
    montantTotal: profsData.filter(p => !p.deja_paye).reduce((acc, p) => acc + p.montant, 0),
  };

  // — Sélection
  const toggleSelection = (id) => {
    setSelection(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };
  const toggleTout = () => {
    const selectionables = profsFiltres.filter(p => p.pret);
    if (selectionables.every(p => selection.has(p.id))) {
      setSelection(prev => { const s = new Set(prev); selectionables.forEach(p => s.delete(p.id)); return s; });
    } else {
      setSelection(prev => { const s = new Set(prev); selectionables.forEach(p => s.add(p.id)); return s; });
    }
  };

  // — Marquer comme payé
  const marquerCommePayes = async () => {
    if (!refVirement.trim()) return alert('Veuillez entrer une référence de virement');
    setMarquerLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Session expirée');

      const profsAMarquer = profsData.filter(p => selection.has(p.id) && p.pret);
      if (profsAMarquer.length === 0) {
        setMarquerLoading(false);
        return alert('Aucun professeur "prêt à payer" sélectionné.');
      }

      // Enregistrement via le BACKEND : le montant est RECALCULÉ en base (sécurité),
      // écriture en service_role, anti-doublon garanti par la contrainte d'unicité.
      const resp = await fetch(`${process.env.REACT_APP_API_URL}/api/payouts/mark-paid`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({
          month: moisSelectionne,
          prof_ids: profsAMarquer.map(p => p.id),
          transfer_reference: refVirement.trim(),
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.success) {
        throw new Error(data.error || 'Échec de l\'enregistrement du paiement');
      }

      // Récapitulatif
      const parts = [];
      if (data.paid?.length) parts.push(`✅ ${data.paid.length} paiement(s) enregistré(s)`);
      if (data.skipped?.length) parts.push(`⏭️ ${data.skipped.length} ignoré(s) : ${data.skipped.map(s => s.reason).join(', ')}`);
      if (data.errors?.length) parts.push(`❌ ${data.errors.length} erreur(s) : ${data.errors.map(e => e.error).join(', ')}`);
      if (parts.length) alert(parts.join('\n'));

      setShowMarquerModal(false);
      setRefVirement('');
      setSelection(new Set());
      await chargerDonnees();
    } catch (e) {
      alert(`Erreur: ${e.message}`);
    } finally {
      setMarquerLoading(false);
    }
  };

  // — Export CSV
  const exporterCSV = async () => {
    setExportLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const profsExport = selection.size > 0
        ? profsData.filter(p => selection.has(p.id))
        : profsFiltres.filter(p => p.pret);

      if (profsExport.length === 0) {
        alert('Aucun professeur à exporter (sélectionnez des profs ou vérifiez les filtres)');
        return;
      }

      // Essayer d'abord le backend
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const res = await fetch(`${apiUrl}/api/payouts/export-csv`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'ngrok-skip-browser-warning': 'true',
          },
          body: JSON.stringify({
            mois: moisSelectionne,
            profs: profsExport.map(p => ({
              id: p.id,
              nom: p.nom,
              prenom: p.prenom,
              email: p.email,
              rib: p.rib_maroc || p.iban_maroc,
              bank: p.bankName,
              titulaire: p.account_holder,
              montant: p.montant,
              sessions: p.sessionCount,
            })),
          }),
        });

        if (res.ok) {
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `payouts_firstarabic_${moisSelectionne}.csv`;
          a.click();
          URL.revokeObjectURL(url);
          return;
        }
      } catch (backendErr) {
        console.warn('Export via backend échoué, génération locale:', backendErr.message);
      }

      // Génération locale (fallback)
      const headers = ['Nom', 'Prénom', 'Email', 'RIB', 'Banque', 'Titulaire', 'Montant (MAD)', 'Séances', 'Mois', 'Statut RIB'];
      const rows = profsExport.map(p => [
        p.nom, p.prenom, p.email,
        (p.rib_maroc || p.iban_maroc || '').replace(/\s/g, ''),
        p.bankName,
        p.account_holder || '',
        p.montant.toFixed(2),
        p.sessionCount,
        moisSelectionne,
        p.ribValide ? 'Valide' : 'INVALIDE',
      ]);

      const bom = '\uFEFF';
      const csv = bom + [headers.join(';'), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payouts_firstarabic_${moisSelectionne}.csv`;
      a.click();
      URL.revokeObjectURL(url);

    } finally {
      setExportLoading(false);
    }
  };

  // ─── Rendu ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ fontFamily: "'Segoe UI', -apple-system, sans-serif", background: '#f8fafc', minHeight: '100vh', color: '#1e293b' }}>

      {/* ── Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1a4a6b 100%)',
        padding: '24px 32px 0', color: 'white',
        boxShadow: '0 4px 20px rgba(15,23,42,0.3)',
      }}>
        <div style={{ maxWidth: 1400, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                <span style={{ fontSize: 28 }}>💸</span>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em' }}>
                  Gestion des Paiements Professeurs
                </h1>
              </div>
              <p style={{ margin: 0, opacity: 0.6, fontSize: 14 }}>
                FirstArabic · Virements bancaires manuels · 80 DH/h (40 DH/30min · 80 DH/h)
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {/* Sélecteur de mois */}
              <select
                value={moisSelectionne}
                onChange={e => setMoisSelectionne(e.target.value)}
                style={{
                  padding: '9px 14px', borderRadius: 9, border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.1)', color: 'white', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {moisOptions.map(o => <option key={o.value} value={o.value} style={{ color: '#1e293b' }}>{o.label}</option>)}
              </select>

              <button onClick={() => setShowTarifsPanel(!showTarifsPanel)} style={{
                padding: '9px 14px', borderRadius: 9, border: '1px solid rgba(255,255,255,0.2)',
                background: showTarifsPanel ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
                color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>
                ⚙️ Tarifs
              </button>

              <button onClick={chargerDonnees} disabled={loading} style={{
                padding: '9px 14px', borderRadius: 9, border: 'none',
                background: loading ? 'rgba(255,255,255,0.2)' : '#3b82f6',
                color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}>
                {loading ? '⏳ Chargement...' : '🔄 Actualiser'}
              </button>
            </div>
          </div>

          {/* Panel tarifs */}
          {showTarifsPanel && (
            <div style={{
              background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: 16, marginBottom: 20,
              border: '1px solid rgba(255,255,255,0.15)', display: 'flex', gap: 20, alignItems: 'center',
            }}>
              <span style={{ fontSize: 13, opacity: 0.8, fontWeight: 600 }}>💰 Tarifs par séance :</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ opacity: 0.8 }}>30 min :</span>
                <input
                  type="number" value={tarifCustom30} min={1} max={100}
                  onChange={e => setTarifCustom30(Number(e.target.value))}
                  style={{ width: 70, padding: '5px 8px', borderRadius: 6, border: 'none', textAlign: 'right', fontWeight: 700 }}
                />
                <span style={{ opacity: 0.6 }}>DH</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ opacity: 0.8 }}>60 min :</span>
                <input
                  type="number" value={tarifCustom60} min={1} max={200}
                  onChange={e => setTarifCustom60(Number(e.target.value))}
                  style={{ width: 70, padding: '5px 8px', borderRadius: 6, border: 'none', textAlign: 'right', fontWeight: 700 }}
                />
                <span style={{ opacity: 0.6 }}>DH</span>
              </label>
              <button onClick={chargerDonnees} style={{
                padding: '6px 14px', background: '#10b981', border: 'none', borderRadius: 7,
                color: 'white', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}>
                Recalculer
              </button>
            </div>
          )}

          {/* Statistiques */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, marginBottom: -1 }}>
            {[
              { label: 'Profs validés', value: stats.totalProfs, icon: '👨‍🏫', color: '#60a5fa' },
              { label: 'À payer ce mois', value: formatMontant(stats.montantTotal), icon: '💶', color: '#34d399' },
              { label: 'Sessions facturables', value: stats.totalSessions, icon: '📚', color: '#a78bfa' },
              { label: 'Erreurs RIB', value: stats.ribManquants, icon: '🚫', color: stats.ribManquants > 0 ? '#f87171' : '#34d399' },
              { label: 'Déjà payés', value: stats.dejaPayes, icon: '✅', color: '#34d399' },
            ].map((s, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.06)', padding: '14px 18px', cursor: 'default',
                borderTop: `3px solid ${s.color}30`,
              }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginTop: 20 }}>
            {[
              { id: 'calcul', label: '💰 Calcul & Paiements' },
              { id: 'historique', label: '📋 Historique' },
              { id: 'erreurs', label: `🔍 Détection d'erreurs ${stats.avecErreurs + stats.avecAvertissements > 0 ? `(${stats.avecErreurs + stats.avecAvertissements})` : ''}` },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                padding: '11px 20px', background: activeTab === tab.id ? 'white' : 'transparent',
                color: activeTab === tab.id ? '#1e293b' : 'rgba(255,255,255,0.7)',
                border: 'none', borderRadius: '10px 10px 0 0', fontWeight: 700,
                fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
                borderBottom: activeTab === tab.id ? '2px solid white' : '2px solid transparent',
              }}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Contenu ── */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 32px' }}>

        {/* Message erreur */}
        {error && (
          <div style={{
            background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 12,
            padding: '14px 18px', marginBottom: 20, color: '#991b1b', display: 'flex', gap: 10, alignItems: 'center',
          }}>
            <span style={{ fontSize: 20 }}>🚨</span>
            <div>
              <div style={{ fontWeight: 700 }}>Erreur de chargement</div>
              <div style={{ fontSize: 13 }}>{error}</div>
            </div>
            <button onClick={chargerDonnees} style={{ marginLeft: 'auto', padding: '6px 14px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 600 }}>
              Réessayer
            </button>
          </div>
        )}

        {/* ── TAB: Calcul & Paiements ── */}
        {activeTab === 'calcul' && (
          <>
            {/* Barre d'actions */}
            <div style={{
              background: 'white', borderRadius: 14, padding: '14px 20px',
              marginBottom: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
            }}>
              {/* Recherche */}
              <div style={{ position: 'relative', flex: '1', minWidth: 200 }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }}>🔍</span>
                <input
                  type="text" placeholder="Rechercher un professeur..."
                  value={search} onChange={e => setSearch(e.target.value)}
                  style={{
                    width: '100%', padding: '9px 12px 9px 34px', borderRadius: 9,
                    border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Filtre statut */}
              <select
                value={filtreStatut}
                onChange={e => setFiltreStatut(e.target.value)}
                style={{ padding: '9px 12px', borderRadius: 9, border: '1px solid #e2e8f0', fontSize: 14, background: 'white', cursor: 'pointer' }}
              >
                <option value="tous">Tous les profs ({profsData.length})</option>
                <option value="avec_sessions">Avec séances ({profsData.filter(p => p.sessionCount > 0).length})</option>
                <option value="pret">Prêts à payer ({profsData.filter(p => p.pret).length})</option>
                <option value="rib_manquant">RIB manquant/invalide ({profsData.filter(p => !p.ribValide).length})</option>
                <option value="paye">Déjà payés ({profsData.filter(p => p.deja_paye).length})</option>
              </select>

              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: filtreErreur ? '#dc2626' : '#64748b', cursor: 'pointer' }}>
                <input type="checkbox" checked={filtreErreur} onChange={e => setFiltreErreur(e.target.checked)} />
                Avec erreurs seulement
              </label>

              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                <button
                  onClick={exporterCSV}
                  disabled={exportLoading}
                  style={{
                    padding: '9px 18px', background: '#0ea5e9', color: 'white', border: 'none',
                    borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    opacity: exportLoading ? 0.6 : 1,
                  }}
                >
                  {exportLoading ? '⏳' : '📥'} Export CSV
                </button>

                {selection.size > 0 && (
                  <button
                    onClick={() => setShowMarquerModal(true)}
                    style={{
                      padding: '9px 18px', background: '#10b981', color: 'white', border: 'none',
                      borderRadius: 9, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    ✅ Marquer payé ({selection.size})
                  </button>
                )}
              </div>
            </div>

            {/* Bilan sélection */}
            {selection.size > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: 12,
                padding: '12px 20px', marginBottom: 16, color: 'white',
                display: 'flex', alignItems: 'center', gap: 16,
              }}>
                <span style={{ fontSize: 20 }}>✅</span>
                <div>
                  <div style={{ fontWeight: 700 }}>
                    {selection.size} professeur(s) sélectionné(s) ·{' '}
                    Total à virer : {formatMontant(profsData.filter(p => selection.has(p.id)).reduce((acc, p) => acc + p.montant, 0))}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>
                    {profsData.filter(p => selection.has(p.id) && !p.pret).length > 0
                      ? `⚠️ ${profsData.filter(p => selection.has(p.id) && !p.pret).length} prof(s) avec erreurs bloquantes inclus dans la sélection`
                      : 'Tous les profs sélectionnés sont prêts à être payés'}
                  </div>
                </div>
                <button onClick={() => setSelection(new Set())} style={{ marginLeft: 'auto', background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 7, padding: '6px 14px', color: 'white', cursor: 'pointer', fontWeight: 600 }}>
                  Tout désélectionner
                </button>
              </div>
            )}

            {/* Table principale */}
            <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'center', width: 40 }}>
                      <input
                        type="checkbox"
                        onChange={toggleTout}
                        checked={profsFiltres.filter(p => p.pret).length > 0 && profsFiltres.filter(p => p.pret).every(p => selection.has(p.id))}
                        style={{ cursor: 'pointer', width: 16, height: 16 }}
                      />
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Professeur</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Séances</th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Calcul</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>RIB</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Banque</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Statut</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Alertes</th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>
                      Chargement des données...
                    </td></tr>
                  ) : profsFiltres.length === 0 ? (
                    <tr><td colSpan={9} style={{ padding: 60, textAlign: 'center', color: '#94a3b8' }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🔍</div>
                      Aucun professeur ne correspond aux filtres
                    </td></tr>
                  ) : profsFiltres.map(prof => {
                    const estSelectionne = selection.has(prof.id);
                    const nbAlertes = prof.erreurs.length + prof.avertissements.length;

                    return (
                      <tr
                        key={prof.id}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          background: estSelectionne ? '#eff6ff' : prof.deja_paye ? '#f0fdf4' : 'white',
                          transition: 'background 0.1s',
                        }}
                      >
                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={estSelectionne}
                            onChange={() => toggleSelection(prof.id)}
                            disabled={prof.deja_paye}
                            style={{ cursor: prof.deja_paye ? 'not-allowed' : 'pointer', width: 16, height: 16 }}
                          />
                        </td>

                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 700, color: '#1e293b', fontSize: 15 }}>
                            {prof.prenom} {prof.nom}
                          </div>
                          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{prof.email}</div>
                          {prof.account_holder && (
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>Titulaire: {prof.account_holder}</div>
                          )}
                        </td>

                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          {prof.sessionCount > 0 ? (
                            <div>
                              <span style={{ fontWeight: 700, fontSize: 18, color: '#6366f1' }}>{prof.sessionCount}</span>
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                {prof.details.seances30 > 0 && `${prof.details.seances30}×30`}
                                {prof.details.seances30 > 0 && prof.details.seances60 > 0 && ' · '}
                                {prof.details.seances60 > 0 && `${prof.details.seances60}×60`}
                              </div>
                            </div>
                          ) : prof.sessionDejaPayees > 0 ? (
                            <div title="Séances déjà incluses dans un payout précédent">
                              <span style={{ fontWeight: 700, fontSize: 15, color: '#94a3b8', textDecoration: 'line-through' }}>{prof.sessionDejaPayees}</span>
                              <div style={{ fontSize: 10, color: '#10b981', marginTop: 2, fontWeight: 600 }}>✓ déjà payées</div>
                            </div>
                          ) : (
                            <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>
                          )}
                        </td>

                        <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                          {prof.montant > 0 ? (
                            <div>
                              <div style={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>{formatMontant(prof.montant)}</div>
                              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                                {prof.details.seances30 > 0 && `${prof.details.seances30}×${tarifCustom30} DH`}
                                {prof.details.seances30 > 0 && prof.details.seances60 > 0 && ' + '}
                                {prof.details.seances60 > 0 && `${prof.details.seances60}×${tarifCustom60} DH`}
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#cbd5e1', fontSize: 13 }}>0 DH</span>
                          )}
                        </td>

                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          {prof.ribValide ? (
                            <div>
                              <Badge type="success">✓ Valide</Badge>
                              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, fontFamily: 'monospace' }}>
                                {prof.coordType ? `${prof.coordType}: ${(prof.rib_maroc || prof.iban_maroc || '').slice(0, 8)}…` : ''}
                              </div>
                            </div>
                          ) : (
                            <Badge type="error">✗ Invalide</Badge>
                          )}
                        </td>

                        <td style={{ padding: '14px 16px', fontSize: 13, color: '#475569' }}>
                          {prof.bankName !== '—' ? (
                            <div style={{ fontWeight: 600 }}>{prof.bankName}</div>
                          ) : (
                            <span style={{ color: '#cbd5e1' }}>—</span>
                          )}
                        </td>

                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          {prof.deja_paye ? (
                            <div>
                              <Badge type="paid">✅ Payé</Badge>
                              {prof.ref_paiement && (
                                <div style={{ fontSize: 10, color: '#15803d', marginTop: 3 }}>Réf: {prof.ref_paiement}</div>
                              )}
                            </div>
                          ) : prof.pret ? (
                            <Badge type="info">🟢 Prêt</Badge>
                          ) : prof.sessionCount === 0 ? (
                            <Badge type="neutral">Pas de séances</Badge>
                          ) : (
                            <Badge type="warning">⚠️ Bloqué</Badge>
                          )}
                        </td>

                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          {nbAlertes > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                              {prof.erreurs.length > 0 && (
                                <Badge type="error">🚫 {prof.erreurs.length} erreur{prof.erreurs.length > 1 ? 's' : ''}</Badge>
                              )}
                              {prof.avertissements.length > 0 && (
                                <Badge type="warning">⚠️ {prof.avertissements.length} avert.</Badge>
                              )}
                            </div>
                          ) : (
                            <span style={{ color: '#86efac', fontSize: 18 }}>✓</span>
                          )}
                        </td>

                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <button
                            onClick={() => setModalProf(prof)}
                            style={{
                              padding: '6px 14px', background: '#f1f5f9',
                              border: '1px solid #e2e8f0', borderRadius: 7,
                              fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#475569',
                            }}
                          >
                            🔍 Voir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {profsFiltres.length > 0 && !loading && (
                <div style={{
                  padding: '12px 20px', borderTop: '2px solid #e2e8f0',
                  background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 13, color: '#64748b' }}>
                    {profsFiltres.length} prof(s) affiché(s) · {profsFiltres.filter(p => p.sessionCount > 0).length} avec séances
                  </span>
                  <div style={{ fontWeight: 800, fontSize: 16, color: '#0f172a' }}>
                    Sous-total affiché : {formatMontant(profsFiltres.filter(p => !p.deja_paye).reduce((acc, p) => acc + p.montant, 0))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ── TAB: Détection d'erreurs ── */}
        {activeTab === 'erreurs' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

              {/* Erreurs bloquantes */}
              <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <div style={{ padding: '16px 20px', background: '#fee2e2', borderBottom: '1px solid #fca5a5' }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 8 }}>
                    🚫 Erreurs bloquantes
                    <Badge type="error">{profsData.filter(p => p.erreurs.length > 0).length}</Badge>
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#7f1d1d' }}>Ces professeurs ne peuvent pas être payés</p>
                </div>
                <div style={{ padding: 16, maxHeight: 400, overflow: 'auto' }}>
                  {profsData.filter(p => p.erreurs.length > 0).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 30, color: '#86efac' }}>
                      <div style={{ fontSize: 32 }}>✅</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#15803d', marginTop: 8 }}>Aucune erreur bloquante !</div>
                    </div>
                  ) : profsData.filter(p => p.erreurs.length > 0).map(prof => (
                    <div key={prof.id} style={{ padding: '12px 14px', borderRadius: 10, background: '#fff5f5', border: '1px solid #fca5a5', marginBottom: 10 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{prof.prenom} {prof.nom}</div>
                      <ErreurPanel erreurs={prof.erreurs} avertissements={[]} />
                      <button onClick={() => setModalProf(prof)} style={{ marginTop: 8, fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                        Voir le détail →
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Avertissements */}
              <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <div style={{ padding: '16px 20px', background: '#fef9c3', borderBottom: '1px solid #fde047' }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#854d0e', display: 'flex', alignItems: 'center', gap: 8 }}>
                    ⚠️ Avertissements
                    <Badge type="warning">{profsData.filter(p => p.avertissements.length > 0 && p.erreurs.length === 0).length}</Badge>
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: '#78350f' }}>Paiement possible mais à vérifier</p>
                </div>
                <div style={{ padding: 16, maxHeight: 400, overflow: 'auto' }}>
                  {profsData.filter(p => p.avertissements.length > 0 && p.erreurs.length === 0).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 30 }}>
                      <div style={{ fontSize: 32 }}>✅</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#15803d', marginTop: 8 }}>Aucun avertissement !</div>
                    </div>
                  ) : profsData.filter(p => p.avertissements.length > 0 && p.erreurs.length === 0).map(prof => (
                    <div key={prof.id} style={{ padding: '12px 14px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde047', marginBottom: 10 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>{prof.prenom} {prof.nom}</div>
                      <ErreurPanel erreurs={[]} avertissements={prof.avertissements} />
                      <button onClick={() => setModalProf(prof)} style={{ marginTop: 8, fontSize: 11, color: '#d97706', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                        Voir le détail →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Résumé global */}
            <div style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 800, color: '#1e293b' }}>📊 Résumé de santé du système</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
                {[
                  { label: 'Profs prêts', value: profsData.filter(p => p.pret).length, total: profsData.length, color: '#10b981', icon: '✅' },
                  { label: 'RIB valides', value: profsData.filter(p => p.ribValide).length, total: profsData.length, color: '#3b82f6', icon: '🏦' },
                  { label: 'Erreurs bloquantes', value: profsData.filter(p => p.erreurs.length > 0).length, total: profsData.length, color: '#ef4444', icon: '🚫', invert: true },
                  { label: 'Sans séances', value: profsData.filter(p => p.sessionCount === 0).length, total: profsData.length, color: '#f59e0b', icon: '📚', invert: true },
                ].map((item, i) => (
                  <div key={i} style={{ background: '#f8fafc', borderRadius: 12, padding: 16, textAlign: 'center' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{item.icon}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, color: item.color }}>{item.value}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{item.label}</div>
                    <div style={{ background: '#e2e8f0', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', borderRadius: 4, background: item.color,
                        width: `${item.total > 0 ? (item.value / item.total) * 100 : 0}%`,
                        transition: 'width 0.5s ease',
                      }} />
                    </div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                      {item.total > 0 ? Math.round((item.value / item.total) * 100) : 0}% des profs
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── TAB: Historique ── */}
        {activeTab === 'historique' && (
          <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#1e293b' }}>📋 Historique des paiements</h3>
              <span style={{ fontSize: 13, color: '#64748b' }}>{historiquePayouts.length} entrées</span>
            </div>
            {historiquePayouts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                <div>Aucun paiement enregistré</div>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Date', 'Professeur', 'Mois', 'Séances', 'Montant', 'Référence virement', 'Méthode', 'Statut'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #e2e8f0' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {historiquePayouts.map((p, i) => (
                    <tr key={p.id || i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>
                        {new Date(p.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                          {p.profs ? `${p.profs.prenom} ${p.profs.nom}` : p.prof_id}
                        </div>
                        {p.profs?.email && <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.profs.email}</div>}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 13, color: '#475569' }}>{p.periode_mois || '—'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 14, fontWeight: 600 }}>{p.sessions_count || '—'}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 800, fontSize: 15, color: '#0f172a' }}>{formatMontant(p.net_amount)}</td>
                      <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: 12, color: '#6366f1' }}>{p.transfer_reference || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12 }}>
                        <Badge type="info">{(p.payout_method || '—').replace('_', ' ')}</Badge>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <Badge type={p.status === 'paid' ? 'paid' : 'warning'}>{p.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* ── Modal Marquer comme payé ── */}
      {showMarquerModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, maxWidth: 480, width: '90%', boxShadow: '0 25px 50px rgba(0,0,0,0.3)' }}>
            <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800 }}>✅ Confirmer le paiement</h2>
            <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: 14 }}>
              Vous êtes sur le point de marquer <strong>{selection.size} professeur(s)</strong> comme payés pour <strong>{moisOptions.find(m => m.value === moisSelectionne)?.label}</strong>.
            </p>

            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 14, marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: '#166534', fontWeight: 700, marginBottom: 6 }}>Récapitulatif</div>
              {profsData.filter(p => selection.has(p.id)).map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#15803d', padding: '3px 0' }}>
                  <span>{p.prenom} {p.nom}</span>
                  <strong>{formatMontant(p.montant)}</strong>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #86efac', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 800, color: '#166534' }}>
                <span>Total</span>
                <span>{formatMontant(profsData.filter(p => selection.has(p.id)).reduce((acc, p) => acc + p.montant, 0))}</span>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 6 }}>
                Référence de virement bancaire *
              </label>
              <input
                type="text"
                value={refVirement}
                onChange={e => setRefVirement(e.target.value)}
                placeholder="Ex: VIR-2025-10-001"
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 9,
                  border: '2px solid #e2e8f0', fontSize: 14, boxSizing: 'border-box',
                  outline: 'none', transition: 'border-color 0.15s',
                }}
                onFocus={e => e.target.style.borderColor = '#3b82f6'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
              />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Cette référence sera stockée pour l'audit et l'historique des paiements
              </div>
            </div>

          {/* Résultat vérification */}
          {verifResult && (
            <div style={{
              padding: '12px 14px', borderRadius: 10, marginBottom: 16,
              background: verifResult.ok ? '#f0fdf4' : '#fee2e2',
              border: `1px solid ${verifResult.ok ? '#86efac' : '#fca5a5'}`,
            }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: verifResult.ok ? '#166534' : '#991b1b', marginBottom: 4 }}>
                {verifResult.ok ? '✅ Calcul vérifié — aucune incohérence' : '❌ Incohérence détectée !'}
              </div>
              {verifResult.details?.map((d, i) => (
                <div key={i} style={{ fontSize: 12, color: d.ok ? '#15803d' : '#dc2626', marginBottom: 2 }}>
                  {d.ok ? '✓' : '✗'} {d.nom} : {d.montantAttendu} DH
                  {!d.ok && ` (affiché: ${d.montantCalcule} DH, écart: ${d.delta?.toFixed(2)} DH)`}
                </div>
              ))}
            </div>
          )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowMarquerModal(false); setRefVirement(''); setVerifResult(null); }} style={{
                padding: '10px 20px', background: '#f1f5f9', border: 'none', borderRadius: 9,
                fontWeight: 600, cursor: 'pointer', fontSize: 14, color: '#475569',
              }}>
                Annuler
              </button>
              <button onClick={async () => {
                const profsAVerifier = profsData.filter(p => selection.has(p.id) && p.pret);
                const details = await Promise.all(profsAVerifier.map(async p => {
                  const v = await verifierCalculAvantPaiement(supabase, p, moisSelectionne);
                  return { nom: `${p.prenom} ${p.nom}`, ...v };
                }));
                setVerifResult({ ok: details.every(d => d.ok), details });
              }} style={{
                padding: '10px 20px', background: '#6366f1', border: 'none', borderRadius: 9,
                fontWeight: 600, cursor: 'pointer', fontSize: 14, color: 'white',
              }}>
                🔍 Vérifier le calcul
              </button>
              <button onClick={marquerCommePayes} disabled={marquerLoading || !refVirement.trim()} style={{
                padding: '10px 24px', background: '#10b981', border: 'none', borderRadius: 9,
                fontWeight: 700, cursor: 'pointer', fontSize: 14, color: 'white',
                opacity: marquerLoading || !refVirement.trim() ? 0.6 : 1,
              }}>
                {marquerLoading ? '⏳ Enregistrement...' : '✅ Confirmer le paiement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal détail prof ── */}
      <ModalDetails
        prof={modalProf}
        sessions={modalProf?.sessions || []}
        onClose={() => setModalProf(null)}
      />
    </div>
  );
}

